import EasyStar from 'easystarjs';

// Grid resolution: 8px per cell. World is 887px wide (matches bunker-bg.png) → 111 columns.
export const GRID_CELL = 8;
const GRID_COLS = 111; // ceil(887 / 8)

// Floor Y positions (pixels) — the standing-floor line of each band in
// bunker-bg.png (where a dweller's feet rest). These include the per-floor
// correction that aligns the sprite feet to the illustration PLUS a uniform
// −10px perspective lift so dwellers read as standing inside the room rather
// than on the very front edge of the floor. Dwellers, the elevator car and book
// piles all share this one source of truth. Keep in sync with BunkerScene.
export const FLOOR_BOTTOMS: Record<number, number> = { 0: 375, 1: 639, 2: 956, 3: 1238, 4: 1554 };

// Room centers per room name — calibrated to bunker-bg.png. Keep in sync with BunkerScene ROOM_X.
export const ROOM_CENTERS: Record<string, { x: number; floor: number }> = {
  "Overseer's Office": { x: 200, floor: 0 },
  Lounge:              { x: 576, floor: 0 },
  'Comm Room':         { x: 382, floor: 1 },
  'Survey Room':       { x: 158, floor: 2 },
  'Archive Vault':     { x: 422, floor: 2 },
  'Data Center':       { x: 642, floor: 2 },
  Workshop:            { x: 200, floor: 3 },
  'QC Lab':            { x: 596, floor: 3 },
  Studio:              { x: 208, floor: 4 },
  'Outreach Bay':      { x: 588, floor: 4 },
};

// Room hit-test rectangles (world coords). Calibrated to bunker-bg.png room interiors.
export const ROOM_RECTS: Record<string, { x0: number; y0: number; x1: number; y1: number }> = {
  "Overseer's Office": { x0: 16,  y0: 150,  x1: 396, y1: 375 },
  Lounge:              { x0: 404, y0: 150,  x1: 748, y1: 375 },
  'Comm Room':         { x0: 16,  y0: 436,  x1: 748, y1: 639 },
  'Survey Room':       { x0: 16,  y0: 718,  x1: 300, y1: 956 },
  'Archive Vault':     { x0: 316, y0: 718,  x1: 528, y1: 956 },
  'Data Center':       { x0: 536, y0: 718,  x1: 748, y1: 956 },
  Workshop:            { x0: 16,  y0: 1028, x1: 388, y1: 1238 },
  'QC Lab':            { x0: 444, y0: 1028, x1: 748, y1: 1238 },
  Studio:              { x0: 16,  y0: 1292, x1: 400, y1: 1554 },
  'Outreach Bay':      { x0: 428, y0: 1292, x1: 748, y1: 1554 },
};

// Elevator shaft center (right channel in bunker-bg.png)
export const ELEVATOR_X = 806;

// Each floor is modelled as a single walkable corridor row at floor level.
// All columns are walkable (0) — visual walls don't block floor-level movement.
function buildGrid(): number[][] {
  return [Array.from({ length: GRID_COLS }, () => 0)];
}

let _finder: EasyStar.js | null = null;

function getFinder(): EasyStar.js {
  if (!_finder) {
    _finder = new EasyStar.js();
    _finder.setGrid(buildGrid());
    _finder.setAcceptableTiles([0]);
    _finder.enableSync();
  }
  return _finder;
}

/** Returns pixel-space waypoints along the floor Y for the given floor. */
export function findFloorPath(
  fromX: number,
  toX: number,
  floor: number
): Array<{ x: number; y: number }> {
  const floorY = FLOOR_BOTTOMS[floor];
  if (floorY === undefined) return [];

  const startCol = Math.round(Math.max(0, Math.min(GRID_COLS - 1, fromX / GRID_CELL)));
  const endCol   = Math.round(Math.max(0, Math.min(GRID_COLS - 1, toX   / GRID_CELL)));

  if (startCol === endCol) return [];

  const finder = getFinder();
  let result: Array<{ x: number; y: number }> | null = null;

  finder.findPath(startCol, 0, endCol, 0, (path) => {
    result = path ? path.map((cell) => ({ x: cell.x * GRID_CELL, y: floorY })) : null;
  });
  finder.calculate();

  return result ?? [];
}

/** Returns the room center position for a given room name. */
export function getRoomCenter(roomName: string): { x: number; floor: number } | null {
  return ROOM_CENTERS[roomName] ?? null;
}
